import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import {
  CkThemeProvider, CkToastProvider, CkConfirmHost, AppShell, Sidebar, Topbar, CommandPalette, IconButton, Avatar,
  useCkToast, useCkTheme, useCkPortal, Spinner, Popover, Menu, OptionChips, PopoverHeader, PopoverBody,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import api from '../services/api.js';
import useNextDesign from './useNextDesign';
import createCkMuiTheme from './ckMuiTheme';
import { visibleNavItems, isPathActive, SYSTEM_LABEL, IS_MISHTAMERET, IS_OPEN_GEMARA } from './adminNav';
import NextDesignTour, { openNextDesignTour } from './NextDesignTour';
import RelatedSettings, { RelatedSettingsButton } from './related/RelatedSettings';
import { relatedPaletteSection } from './related/relatedPalette';
import { QUICK_CREATE_PARAM } from './quickCreate';
import './nextGlobals.css';

/**
 * NextAdminShell — the new (crm-kit/next) chrome of the admin interface, built like Turbo's module shells:
 * sidebar groups + fixed settings hub, one slim top bar (module, Ctrl+K search, "חדש" quick actions with
 * Alt+1.. and pinning, related settings Ctrl+,, notifications), personal menu (display mode, tour, back to the
 * old design, logout), toasts and ckConfirm.
 * The menu is the same permission-aware list as the old sidebar (adminNav) — nothing is lost, only grouped.
 */
const ACCENT = '#4F46E5';
const GROUPS = [
  { key: 'top', title: null, ids: ['dashboard', 'calendar', 'reports'] },
  { key: 'people', title: 'אנשים', ids: ['members', 'broadcasts', 'pending-registrations', 'kolels'] },
  { key: 'american', title: 'מבחנים אמריקאים', children: 'american-exams' },
  { key: 'examinees', title: 'נבחנים', children: 'examinees' },
  { key: 'exams', title: 'מבחנים', ids: ['exams', 'registrations', 'exam-periods', 'exam-cities', 'exam-library', 'gabbai-orders', 'gemara-orders', 'labels'] },
  { key: 'content', title: 'תוכן ותקשורת', ids: ['content', 'narration-catalog', 'ivr', 'ivr-templates', 'email-automations', 'subscriptions'] },
];
const SETTINGS_IDS = ['users', 'roles', 'email-domain', 'feature-flags', 'audit-log'];
const PINS_KEY = 'bchinot-next-qa-pins';
const MODE_OPTIONS = [{ value: 'light', label: 'בהיר' }, { value: 'dark', label: 'כהה' }, { value: 'system', label: 'לפי המחשב' }];
const STYLE_OPTIONS = [{ value: 'modern', label: 'מודרני' }, { value: 'classic', label: 'קלאסי (ניסיוני)' }, { value: 'retro', label: 'רטרו (ניסיוני)' }];

export default function NextAdminShell({ children }) {
  const nextDesign = useNextDesign();
  return (
    <CkThemeProvider style={nextDesign.style} mode={nextDesign.mode} accent={ACCENT} className="bchinot-next">
      <CkToastProvider>
        <MuiBridge>
          <ShellInner nextDesign={nextDesign}>{children}</ShellInner>
          {/* answers ckConfirm() — screens fall back to window.confirm in the old design */}
          <CkConfirmHost />
          <NextDesignTour />
        </MuiBridge>
      </CkToastProvider>
    </CkThemeProvider>
  );
}

/** MUI screens inside the new design: themed from the ck tokens, modals/poppers rendered into the ck portal */
function MuiBridge({ children }) {
  const { style, resolvedMode } = useCkTheme();
  const portal = useCkPortal();
  const portalRef = useRef(portal);
  portalRef.current = portal;
  const theme = useMemo(() => createCkMuiTheme(style, resolvedMode, () => portalRef.current), [style, resolvedMode]);
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = theme.palette.background.default;
    document.documentElement.style.colorScheme = theme.palette.mode;
    return () => {
      document.body.style.backgroundColor = prev;
      document.documentElement.style.colorScheme = '';
    };
  }, [theme]);
  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
}

function readPins(defaults) {
  try { return JSON.parse(localStorage.getItem(PINS_KEY)) || defaults; } catch { return defaults; }
}

function ShellInner({ children, nextDesign }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useCkToast();
  const { user, logout, hasPermission } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const nav = useMemo(() => visibleNavItems(user, hasPermission), [user, hasPermission]);
  const byId = useMemo(() => new Map(nav.map((it) => [it.id, it])), [nav]);
  const canSeePending = byId.has('pending-registrations');

  // בקשות רישום שממתינות — פעמון בסרגל העליון + מונה בתפריט
  useEffect(() => {
    if (!canSeePending) return undefined;
    let cancelled = false;
    const load = () => api.get('/pending-registrations', { params: { status: 'pending', page: 1, pageSize: 1 } })
      .then(({ data }) => { if (!cancelled) setPendingCount(Number(data?.total) || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(t); };
  }, [canSeePending, location.pathname]);

  const toItem = useCallback((it, icon) => ({
    key: it.id,
    label: it.label,
    icon: icon || it.icon || 'list',
    href: it.path,
    badge: it.id === 'pending-registrations' && pendingCount ? pendingCount : undefined,
    active: isPathActive(location.pathname, it.path),
    raw: it,
  }), [location.pathname, pendingCount]);

  /* ── sidebar groups (anything not placed still appears under "עוד" — never lose a screen) ── */
  const groups = [];
  const used = new Set(SETTINGS_IDS);
  GROUPS.forEach((g) => {
    if (g.children) {
      const parent = byId.get(g.children);
      if (!parent) return;
      used.add(parent.id);
      groups.push({ key: g.key, title: g.title, items: parent.children.map((c) => toItem({ ...c }, parent.icon)) });
      return;
    }
    const items = g.ids.map((id) => byId.get(id)).filter(Boolean);
    items.forEach((it) => used.add(it.id));
    if (items.length) groups.push({ key: g.key, title: g.title, items: items.map((it) => toItem(it)) });
  });
  const extra = nav.filter((it) => !used.has(it.id));
  if (extra.length) groups.push({ key: 'more', title: 'עוד', items: extra.map((it) => toItem(it)) });
  const settingsLinks = SETTINGS_IDS.map((id) => byId.get(id)).filter(Boolean);

  /* ── every screen this user can open (palette, related settings) ── */
  const allScreens = useMemo(() => {
    const out = [];
    nav.forEach((it) => {
      if (it.children) it.children.forEach((c) => out.push({ key: c.id, label: `${it.label} · ${c.label}`, icon: it.icon, href: c.path }));
      else out.push({ key: it.id, label: it.label, icon: it.icon, href: it.path });
    });
    out.push({ key: 'profile', label: 'אזור אישי', icon: 'user', href: '/profile' });
    return out;
  }, [nav]);

  const go = useCallback((it) => {
    const raw = it.raw || it;
    navigate(raw.path || raw.href);
  }, [navigate]);
  const create = useCallback((path) => navigate(`${path}?${QUICK_CREATE_PARAM}=1`), [navigate]);

  /* ── quick actions (global, Alt+1..) — only what the user may create ── */
  const quickActions = useMemo(() => [
    byId.has('members') && hasPermission('members', 'create')
      ? { key: 'member', label: 'חבר', icon: 'users', tone: 'primary', onClick: () => create('/members') } : null,
    byId.has('registrations') && hasPermission('examRegistration', 'create')
      ? { key: 'registration', label: 'הרשמה למבחן', icon: 'note', tone: 'success', onClick: () => create('/registrations') } : null,
    byId.has('exams') && hasPermission('exams', 'create') && !IS_MISHTAMERET && !IS_OPEN_GEMARA
      ? { key: 'exam', label: 'מבחן', icon: 'task', tone: 'warning', onClick: () => create('/exams') } : null,
    byId.has('american-exams') && hasPermission('exams', 'create')
      ? { key: 'american-exam', label: 'מבחן אמריקאי', icon: 'check', tone: 'warning', onClick: () => create('/american-exams/daf_yomi') } : null,
    byId.has('kolels') && hasPermission('kolels', 'create')
      ? { key: 'kolel', label: 'כולל', icon: 'building', tone: 'info', onClick: () => create('/kolels') } : null,
    byId.has('users') && hasPermission('users', 'create')
      ? { key: 'user', label: 'משתמש', icon: 'key', tone: 'violet', onClick: () => create('/users') } : null,
  ].filter(Boolean).map((a, i) => ({ ...a, kbd: i < 9 ? `Alt+${i + 1}` : undefined })), [byId, hasPermission, create]);

  const [pinned, setPinned] = useState(() => readPins(['member', 'registration', 'american-exam']));
  useEffect(() => { try { localStorage.setItem(PINS_KEY, JSON.stringify(pinned)); } catch { /* ignore */ } }, [pinned]);

  /* ── command palette: people, kolels, exams, actions, screens, settings ── */
  const getResults = useCallback(async (q) => {
    const query = q.trim();
    const actions = quickActions.filter((a) => !query || a.label.includes(query) || 'חדש'.includes(query))
      .map((a) => ({ key: `a-${a.key}`, label: `${a.label} — חדש`, icon: a.icon, kbd: a.kbd, onSelect: a.onClick }));
    const screens = allScreens.filter((s) => !query || s.label.includes(query)).slice(0, query ? 8 : 6)
      .map((s) => ({ key: `s-${s.key}`, label: s.label, icon: s.icon, sub: 'מסך', onSelect: () => navigate(s.href) }));
    let people = [];
    let kolels = [];
    let exams = [];
    if (query.length >= 2) {
      const [m, k, e] = await Promise.allSettled([
        byId.has('members') ? api.get('/members', { params: { search: query, page: 1, pageSize: 6 } }) : Promise.resolve(null),
        byId.has('kolels') ? api.get('/kolels', { params: { search: query, page: 1, pageSize: 4 } }) : Promise.resolve(null),
        byId.has('exams') ? api.get('/exams', { params: { search: query, page: 1, pageSize: 4 } }) : Promise.resolve(null),
      ]);
      const rows = (r) => (r.status === 'fulfilled' ? (r.value?.data?.data || []) : []);
      people = rows(m).map((p) => {
        const name = [p.lastName, p.firstName].filter(Boolean).join(' ') || p.memberNumber || '';
        return {
          key: `p-${p._id}`,
          label: name,
          sub: [p.phone, p.city, p.idNumber].filter(Boolean).join(' · '),
          leading: <Avatar text={name} size={26} />,
          onSelect: () => navigate(`/members/${p._id}`),
        };
      });
      kolels = rows(k).map((x) => ({
        key: `k-${x._id}`, label: x.name, sub: [x.code, x.city].filter(Boolean).join(' · '), icon: 'building', onSelect: () => navigate(`/kolels/${x._id}`),
      }));
      exams = rows(e).map((x) => ({
        key: `e-${x._id}`, label: x.name || x.code, sub: [x.code, x.subject].filter(Boolean).join(' · '), icon: 'task', onSelect: () => navigate(`/exams/${x._id}`),
      }));
    }
    const settings = relatedPaletteSection(allScreens, query, navigate);
    return [
      { title: 'חברים', items: people },
      { title: 'כוללים', items: kolels },
      { title: 'מבחנים', items: exams },
      { title: 'פעולות', items: actions },
      { title: 'מסכים', items: screens },
      ...(settings ? [settings] : []),
    ];
  }, [quickActions, allScreens, navigate, byId]);

  /* ── personal menu ── */
  const userName = user?.username || '';
  const roleLabel = (user?.roles || []).includes('admin') ? 'מנהל מערכת' : (user?.roles || [])[0] || '';
  const doLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };
  const userMenu = [
    { title: userName },
    { key: 'personal', label: 'אזור אישי', icon: 'user', onClick: () => navigate('/profile') },
    { divider: true },
    { title: 'מצב תצוגה' },
    ...[
      { key: 'light', label: 'בהיר', icon: 'sun' },
      { key: 'dark', label: 'כהה', icon: 'moon' },
      { key: 'system', label: 'לפי המחשב', icon: 'sliders' },
    ].map((m) => ({ key: `mode-${m.key}`, label: m.label, icon: m.icon, active: nextDesign.mode === m.key, end: nextDesign.mode === m.key ? '✓' : null, onClick: () => nextDesign.setMode(m.key) })),
    { divider: true },
    { key: 'search', label: 'חיפוש מהיר', icon: 'search', end: 'Ctrl K', onClick: () => setPaletteOpen(true) },
    { key: 'tour', label: 'סיור בעיצוב החדש', icon: 'sparkle', onClick: () => openNextDesignTour() },
    { key: 'old', label: 'חזרה לעיצוב הקודם', icon: 'history', onClick: () => nextDesign.setEnabled(false) },
    { key: 'logout', label: 'התנתקות', icon: 'logout', danger: true, onClick: doLogout },
  ];

  const bottomItems = settingsLinks.length ? [{
    key: '__settings',
    label: 'הגדרות',
    icon: 'gear',
    onClick: (e) => setSettingsAnchor(e.currentTarget),
    active: settingsLinks.some((s) => isPathActive(location.pathname, s.path)),
  }] : [];

  const sidebar = (
    <Sidebar
      org={{ name: SYSTEM_LABEL, sub: 'ממשק ניהול' }}
      groups={groups}
      bottomItems={bottomItems}
      user={{ name: userName, role: roleLabel }}
      userMenu={userMenu}
      onNavigate={go}
    />
  );

  const topbar = (
    <Topbar
      onMenu={() => setNavOpen(true)}
      modules={[{ key: 'admin', label: SYSTEM_LABEL, color: ACCENT }]}
      activeModule="admin"
      onModuleChange={() => navigate(byId.has('dashboard') ? '/dashboard' : (nav[0]?.path || '/profile'))}
      onSearch={() => setPaletteOpen(true)}
      searchLabel="חיפוש"
      quickActions={quickActions}
      pinnedActions={pinned.filter((k) => quickActions.some((a) => a.key === k))}
      onPinnedChange={setPinned}
      onPinLimit={(n) => toast(`אפשר להצמיד עד ${n} קיצורים`)}
      end={(
        <>
          <RelatedSettingsButton />
          {canSeePending ? (
            <IconButton
              icon="bell"
              label={pendingCount ? `${pendingCount} בקשות רישום ממתינות` : 'בקשות רישום חדשים'}
              badge={pendingCount || undefined}
              onClick={() => navigate('/pending-registrations')}
            />
          ) : null}
        </>
      )}
    />
  );

  return (
    <>
      <AppShell sidebar={sidebar} topbar={topbar} sidebarOpen={navOpen} onSidebarClose={() => setNavOpen(false)}>
        {/* page-level suspense: the shell stays put while a screen loads */}
        <Suspense fallback={<div className="ck-empty"><Spinner size={28} /></div>}>
          <div className="bchinot-page">{children}</div>
        </Suspense>
      </AppShell>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} getResults={getResults} placeholder="חפש חבר, כולל, מבחן, מסך או פעולה…" />
      <RelatedSettings screens={allScreens} nextDesign={nextDesign} />
      <Popover open={!!settingsAnchor} anchorEl={settingsAnchor} onClose={() => setSettingsAnchor(null)} width={300}>
        <PopoverHeader icon="gear" title="הגדרות" sub="כל ההגדרות במקום אחד" onClose={() => setSettingsAnchor(null)} />
        <Menu items={settingsLinks.map((l) => ({ key: l.id, label: l.label, icon: l.icon || 'gear', onClick: () => { setSettingsAnchor(null); go(l); } }))} />
        <PopoverBody>
          <div className="ck-optgroup">
            <b>מצב תצוגה</b>
            <OptionChips value={nextDesign.mode} onChange={nextDesign.setMode} options={MODE_OPTIONS} />
          </div>
          <div className="ck-optgroup" style={{ marginBottom: 0 }}>
            <b>סגנון</b>
            <OptionChips value={nextDesign.style} onChange={nextDesign.setStyle} options={STYLE_OPTIONS} />
            {nextDesign.style !== 'modern' ? <div className="ck-hint">מצב כהה זמין כרגע בסגנון המודרני בלבד</div> : null}
          </div>
        </PopoverBody>
      </Popover>
    </>
  );
}
